/**
 * Pinning the cancel sheet to the visible viewport while the on-screen keyboard
 * is open.
 *
 * WHY THIS IS IMPERATIVE AND NOT AN INLINE `style` PROP — this is the whole bug:
 *
 * The widget's Tailwind runs with `important: true` (tailwind.config.js), so
 * EVERY utility class compiles with `!important` — including the sheet's own
 * `h-full` (`height: 100% !important`) and the base sheet's `bottom-0` /
 * `h-auto`. A React/Preact inline `style` prop emits a plain declaration with no
 * priority, and a normal inline declaration LOSES to an `!important` rule from a
 * stylesheet. So the keyboard geometry was computed correctly, handed to the
 * `style` prop, and silently discarded by the cascade. The sheet stayed at the
 * full layout-viewport height and never shrank for the keyboard.
 *
 * That is why five previous widget-side fixes all failed identically and the
 * problem looked environmental: every one of them set geometry inline or via a
 * class, and `important: true` defeated all of them regardless of the approach.
 *
 * The fix is priority, not geometry. An `!important` declaration in the style
 * ATTRIBUTE outranks an `!important` declaration from an author stylesheet, so
 * `setProperty(..., "important")` is the one form that actually lands.
 */

/** The properties we take over while the keyboard is up, and hand back after. */
const GEOMETRY = [
  "top",
  "bottom",
  "height",
  "max-height",
  "min-height",
] as const;

export interface KeyboardViewport {
  keyboardOpen: boolean;
  /** Top of the visible viewport, in layout-viewport pixels. */
  offsetTop: number;
  /** Height of the visible viewport, or null when it can't be read. */
  visibleHeight: number | null;
}

/** What we read off `window` and `window.visualViewport` each measurement. */
export interface ViewportMetrics {
  /** `window.innerHeight` — the LAYOUT viewport; iOS never shrinks it for keys. */
  layoutHeight: number;
  /** `visualViewport.height` — the visible rectangle. */
  visualHeight: number;
  /** `visualViewport.offsetTop` — where that rectangle sits in the layout one. */
  visualOffsetTop: number;
}

/** Below this, the inset is browser-chrome jitter (URL bar collapse), not keys. */
const KEYBOARD_MIN_INSET = 60;

/**
 * Derive the keyboard state from a single set of viewport metrics.
 *
 * THE KEYBOARD INSET MUST NOT SUBTRACT `offsetTop` — that was the bug.
 *
 * The sheet is a modal Base UI dialog, so the body is scroll-locked and the page
 * CANNOT scroll. When the keyboard hides the focused composer, iOS therefore
 * reveals it by panning the VISUAL viewport down inside the layout viewport —
 * `offsetTop` rises to roughly the keyboard's height. Subtracting it made the
 * inset collapse to ~0 at exactly the moment the keyboard was fully up, so we
 * concluded the keyboard was CLOSED, released the geometry, and let the sheet
 * spring back to `h-full` (the full, unsafe viewport). Panning back to the top
 * drove offsetTop to 0, the inset jumped back to the real keyboard height, and
 * the sheet snapped to the right size — the "it only fixes itself when I scroll
 * all the way up, with a flicker" symptom.
 *
 * `layoutHeight - visualHeight` is the keyboard, wherever the visual viewport
 * happens to be panned to.
 */
export function readKeyboardViewport(m: ViewportMetrics): KeyboardViewport {
  const inset = Math.max(0, Math.round(m.layoutHeight - m.visualHeight));
  return {
    keyboardOpen: inset > KEYBOARD_MIN_INSET,
    offsetTop: Math.round(m.visualOffsetTop),
    visibleHeight: Math.round(m.visualHeight),
  };
}

/**
 * Pin `el` to exactly the visible rectangle while the keyboard is open, and
 * release it entirely when it closes. Releasing means REMOVING the properties,
 * not setting them back to a guessed value — the classes then take over again,
 * which is what we want.
 */
export function applyKeyboardGeometry(
  el: HTMLElement,
  vp: KeyboardViewport,
): void {
  if (!vp.keyboardOpen || vp.visibleHeight == null) {
    for (const prop of GEOMETRY) el.style.removeProperty(prop);
    return;
  }
  // Every one of these MUST carry `important` or the class wins. `max-height`
  // and `min-height` are cleared too: a `max-h-[90dvh] !important` left standing
  // would clamp the sheet back to the layout viewport we are trying to escape.
  el.style.setProperty("top", `${vp.offsetTop}px`, "important");
  el.style.setProperty("bottom", "auto", "important");
  el.style.setProperty("height", `${vp.visibleHeight}px`, "important");
  el.style.setProperty("max-height", "none", "important");
  el.style.setProperty("min-height", "0", "important");
}
